import { productListNoPage, queryFleetProducts, queryFleetDevices, countFleetDevices } from '@/api/visual'
import { isRequestCanceled } from '@/api/http'
import { encodeQueryParam } from '@/utils/utils'
import { validateListData, selectedProductId } from './model'
import { listPageSize, listKeyword } from './paging'

function readResult (response) {
  const body = response && response.data
  if (!response || response.status !== 200 || !body || body.success === false || (body.status && body.status !== 200)) {
    throw new Error(body && body.message || '平台数据读取失败')
  }
  return body.result
}

function checkedRows (kind, rows) {
  const error = validateListData(kind, rows, Infinity)
  if (error) throw new Error('平台数据格式错误：' + error)
  return rows
}

function mapRows (kind, rows) {
  if (!Array.isArray(rows)) throw new Error('平台列表响应格式不正确')
  return checkedRows(kind, rows.map(row => {
    if (!row || typeof row !== 'object') throw new Error('平台列表响应格式不正确')
    if (kind === 'product') return { id: row.id, name: row.name }
    const state = row.state && typeof row.state === 'object' ? row.state.value : row.state
    return {
      id: row.id, name: row.name, productId: row.productId, productName: row.productName,
      state: ['online', 'offline', 'notActive'].includes(state) ? state : 'unknown'
    }
  }))
}

// 仅用于编辑器的产品范围选择器，不用于大屏列表加载设备。
export async function fetchPlatformProducts (requestOptions = {}, ids) {
  const terms = ids ? { 'id$IN': ids } : {}
  return mapRows('product', readResult(await productListNoPage(encodeQueryParam({ terms }), requestOptions)))
}

export function fleetPageQuery (kind, option = {}, context = {}, editing = false, pageIndex = 0, keyword = '') {
  const terms = []
  if (kind === 'device' && option.productScope !== 'all') {
    const id = selectedProductId(option, context, editing)
    if (!id) return null
    terms.push({ column: 'productId', termType: 'eq', value: id })
  } else if (kind === 'product' && option.productScope === 'specified') {
    const ids = Array.isArray(option.productIds) ? option.productIds : []
    if (!ids.length) return null
    terms.push({ column: 'id', termType: 'in', value: ids })
  }
  const text = listKeyword(keyword)
  // 产品范围 AND (名称 OR 编号)，避免 OR 条件绕过当前产品范围。
  if (text) terms.push({ terms: [
    { column: 'name', termType: 'like', value: '%' + text + '%' },
    { column: 'id', termType: 'like', value: '%' + text + '%', type: 'or' }
  ] })
  return { paging: true, pageIndex: Number.isSafeInteger(pageIndex) && pageIndex >= 0 ? pageIndex : 0,
    pageSize: listPageSize(option), sorts: [{ name: 'id', order: 'asc' }], terms }
}

async function productCounts (products, requestOptions) {
  let cursor = 0, statsError = false
  const jobs = products.flatMap(row => ['deviceCount', 'onlineCount'].map(key => ({ row, key })))
  // 统计只查询当前批产品，最多四个并行请求，不下载整个产品下的设备。
  await Promise.all(Array.from({ length: Math.min(4, jobs.length) }, async () => {
    while (cursor < jobs.length) {
      const { row, key } = jobs[cursor++]
      const terms = [{ column: 'productId', termType: 'eq', value: row.id }]
      if (key === 'onlineCount') terms.push({ column: 'state', termType: 'eq', value: 'online' })
      try {
        const count = readResult(await countFleetDevices({ terms }, requestOptions))
        if (!Number.isSafeInteger(count) || count < 0) throw new Error('设备统计响应格式不正确')
        row[key] = count
      } catch (error) {
        if (isRequestCanceled(error)) throw error
        statsError = true
      }
    }
  }))
  products.forEach(row => {
    if (row.onlineCount > row.deviceCount) { delete row.onlineCount; statsError = true }
  })
  return statsError
}

export async function fetchPlatformPage (kind, option = {}, context = {}, editing = false, pageIndex = 0, keyword = '', requestOptions = {}, onProducts, includeCounts = true) {
  const query = fleetPageQuery(kind, option, context, editing, pageIndex, keyword)
  if (!query) return { rows: [], total: 0, pageIndex: 0, hasMore: false, statsError: false }
  const result = readResult(await (kind === 'product' ? queryFleetProducts : queryFleetDevices)(query, requestOptions))
  if (!result || !Array.isArray(result.data) || !Number.isSafeInteger(result.total) || result.total < result.data.length || result.data.length > query.pageSize) {
    throw new Error('平台分页响应格式不正确')
  }
  const rows = mapRows(kind, result.data)
  const page = { rows, total: result.total, pageIndex: query.pageIndex,
    hasMore: rows.length > 0 && (query.pageIndex + 1) * query.pageSize < result.total, statsError: false }
  // 产品名称先显示；慢统计不能让整个产品列表一直空白。
  if (kind === 'product' && rows.length && onProducts) onProducts(page)
  const statsError = kind === 'product' && rows.length && includeCounts ? await productCounts(rows, requestOptions) : false
  return { ...page, rows: rows.map(row => ({ ...row })), statsError }
}
